import { useEffect, useState, useMemo } from "react";import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowDown,
  Check,
  ChevronDown,
  ShieldCheck,
  Sparkles,
  Users,
  TrendingUp,
  Clock,
  X as XIcon,
  Plus as PlusIcon,
  Minus as MinusIcon,
  Heart,
  Eye,
  Wrench,
} from "lucide-react";
import api from "@/lib/api";
import { getListOverride, getAttributionPayload, getUtm, getClickIds, getEverflowSub1 } from "@/lib/utm";
import { track, trackScrollDepth, trackSectionDwell } from "@/lib/analytics";
import { grantAllConsent } from "@/lib/tracking";
import { BRAND_LOGO, BOOK_MOCKUP, COMMUNITY_MOCKUP } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import TrackingLoader from "@/components/app/TrackingLoader";
import { mergeSections } from "@/lib/sections";

const STAT_ICONS = [TrendingUp, Clock, Users, ShieldCheck];

export default function LandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  // Marketing consent — default checked (matches Israeli §30א(ג): registering for the service = implicit opt-in;
  // the checkbox makes it explicit and auditable + gives the user a way to decline).
  const [marketingConsent, setMarketingConsent] = useState(true);

  const sections = useMemo(
    () => (campaign ? mergeSections(campaign.sections).filter((s) => s.enabled !== false) : []),
    [campaign]
  );

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams();
        if (slug) params.set("slug", slug);
        // On the root landing (no /c/:slug), let ?sub1=<affiliate_slug> resolve to
        // that affiliate's master campaign — so every affiliate can drive traffic
        // to `emetohovot.co.il/?sub1=<slug>` without knowing the funnel id.
        const sub1 = getEverflowSub1();
        if (!slug && sub1) params.set("sub1", sub1);
        const qs = params.toString();
        const url = `/campaigns/public/default${qs ? `?${qs}` : ""}`;
        const { data } = await api.get(url);
        setCampaign(data);
        track("page_view", { campaignId: data.id, payload: { page: "landing", slug: data.slug } });
      } catch (e) {
        toast.error("שגיאה בטעינת הקמפיין");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!campaign) return;
    window.__currentCampaignId = campaign.id;
    const c1 = trackScrollDepth(campaign.id);
    const c2 = trackSectionDwell(campaign.id);
    return () => {
      c1();
      c2();
    };
  }, [campaign]);

  const handleFormStart = () => {
    if (!campaign) return;
    if (!sessionStorage.getItem("form_started_fired")) {
      sessionStorage.setItem("form_started_fired", "1");
      track("form_started", { campaignId: campaign.id });
      // Fire Meta Pixel early — user showed intent by interacting with the form,
      // even if they haven't accepted cookies yet. Safe no-op if no pixel configured.
      if (typeof window.__forceLoadMetaPixel === "function") {
        window.__forceLoadMetaPixel();
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!campaign || submitting) return;
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("יש למלא את כל השדות");
      return;
    }
    setSubmitting(true);
    // User submitted the form — treat that as informed consent and unlock all trackers.
    // This must run BEFORE the API call so GA4 / Pixel are alive when we fire conversion events.
    grantAllConsent();
    try {
      const attribution = getAttributionPayload();
      const listOverride = getListOverride();
      const { data } = await api.post("/leads", {
        campaign_id: campaign.id,
        list_id: listOverride || null,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        marketing_consent: marketingConsent,
        ...attribution,
      });
      track("form_submitted_success", {
        campaignId: campaign.id,
        leadId: data.lead_id,
        payload: { list_id: data.list_id },
      });
      // Fire Meta Pixel `Lead` conversion event — pixel is guaranteed loaded by now
      if (typeof window.fbq === "function") {
        try { window.fbq("track", "Lead"); } catch (err) { console.warn("fbq Lead failed", err?.message); }
      }
      toast.success("נרשמת בהצלחה! מעבירים אותך...");

      // Post-signup destination — driven by campaign settings.
      //   "affiliate_redirect" → external URL (campaign.affiliate_url) with UTMs appended
      //   "questionnaire"      → interactive financial questionnaire
      //   default              → 4-step guide
      const postSignup = campaign?.post_signup_page || "guide";
      if (postSignup === "affiliate_redirect" && campaign?.affiliate_url) {
        // Forward UTMs / click IDs / lead_id to the affiliate URL so the partner can attribute
        // and dedupe on their side (useful for postbacks / CAPI). Preserves existing query params
        // on the affiliate URL if any.
        try {
          const utms = { ...getUtm(), ...getClickIds() };
          const target = new URL(campaign.affiliate_url);
          Object.entries(utms).forEach(([k, v]) => {
            if (v && !target.searchParams.has(k)) target.searchParams.set(k, v);
          });
          target.searchParams.set("lead_id", data.lead_id);
          setTimeout(() => {
            window.location.href = target.toString();
          }, 600);
          return;
        } catch (err) {
          // Bad affiliate URL (unparseable) → fall through to guide.
          console.warn("affiliate_url parse failed, falling back to guide", err?.message);
        }
      }
      const destination = postSignup === "questionnaire"
        ? `/q/${campaign.slug}/${data.lead_id}`
        : postSignup === "affiliate_pitch"
          ? `/pitch/${data.lead_id}`
          : `/funnel/${data.lead_id}/1`;
      setTimeout(() => navigate(destination), 600);
    } catch (e) {
      toast.error("שגיאה בשליחת הפרטים, נסו שוב");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
        <div className="w-12 h-12 rounded-full border-4 border-brand-amber border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf9] text-brand-charcoal" data-testid="landing-page">
      <TrackingLoader slug={campaign.slug} listId={getListOverride()} />
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <img src={BRAND_LOGO} alt={campaign.brand_name} className="h-10 w-auto" data-testid="brand-logo" />
          <a
            href="#hero-form"
            data-testid="header-cta"
            className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-brand-charcoal text-white text-sm font-bold hover:bg-brand-stone transition"
          >
            התחילו עכשיו
          </a>
        </div>
      </header>

      {/* HERO (locked: always first, contains the lead form) */}
      <HeroSection
        section={sections.find((s) => s.id === "hero") || {}}
        campaign={campaign}
        form={form}
        setForm={setForm}
        submit={submit}
        submitting={submitting}
        handleFormStart={handleFormStart}
        marketingConsent={marketingConsent}
        setMarketingConsent={setMarketingConsent}
      />

      {/* Dynamic sections (order driven by campaign.sections) */}
      {sections.filter((s) => s.id !== "hero").map((s) => (
        <SectionRenderer key={s.id} section={s} campaign={campaign} />
      ))}

      {/* Footer */}
      <footer id="footer" className="bg-brand-stone text-stone-300">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-3">
            <img src={BRAND_LOGO} alt="" className="h-8 w-auto opacity-90 invert" />
            <span>© 2026 {campaign.brand_name}. כל הזכויות שמורות.</span>
          </div>
          <div className="flex gap-4 flex-wrap justify-center">
            <a href={slug ? `/c/${slug}/terms` : "/terms"} className="hover:text-white" data-testid="footer-terms-link">תקנון</a>
            <a href={slug ? `/c/${slug}/privacy` : "/privacy"} className="hover:text-white" data-testid="footer-privacy-link">מדיניות פרטיות</a>
            <a href={slug ? `/c/${slug}/contact` : "/contact"} className="hover:text-white" data-testid="footer-contact-link">יצירת קשר</a>
            <button
              type="button"
              onClick={() => window.openAccessibilityStatement && window.openAccessibilityStatement()}
              className="hover:text-white"
              data-testid="footer-a11y-statement"
            >
              הצהרת נגישות
            </button>
            <button
              type="button"
              onClick={() => window.openCookiePreferences && window.openCookiePreferences()}
              className="hover:text-white"
              data-testid="footer-cookie-settings"
            >
              הגדרות עוגיות
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================
// Section renderers — order/visibility driven by campaign.sections
// ============================================================

function HeroSection({ section, campaign, form, setForm, submit, submitting, handleFormStart, marketingConsent, setMarketingConsent }) {
  return (
    <section data-section="hero" id="main-content" className="relative overflow-hidden">
      <div className="absolute inset-0 amber-halo opacity-60 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-10 md:pt-20 pb-12 md:pb-24 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-right min-w-0 order-2 md:order-1"
        >
          {section.eyebrow ? (
            <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-brand-amber font-bold text-xs sm:text-sm mb-4 sm:mb-6">
              {section.eyebrow}
            </span>
          ) : null}
          <h1
            data-testid="hero-title"
            className="text-[28px] sm:text-4xl md:text-5xl lg:text-[58px] font-[800] leading-[1.15] md:leading-[1.1] tracking-tight text-brand-charcoal break-words"
          >
            {campaign.hero_title}
          </h1>
          <p data-testid="hero-subtitle" className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-stone-600 leading-[1.6]">
            {campaign.hero_subtitle}
          </p>
          {section.body ? (
            <p
              data-testid="hero-body"
              className="mt-4 text-base sm:text-lg text-stone-700 leading-[1.75] whitespace-pre-line"
            >
              {section.body}
            </p>
          ) : null}

          <form
            id="hero-form"
            onSubmit={submit}
            className="mt-6 md:mt-8 bg-white rounded-3xl shadow-[0_12px_40px_rgba(28,25,23,0.08)] border border-black/5 p-5 md:p-7"
            data-testid="lead-form"
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-bold">שם פרטי</Label>
                <Input
                  id="name"
                  data-testid="form-input-name"
                  placeholder="הקלידו שם"
                  value={form.name}
                  onFocus={handleFormStart}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 h-12 rounded-xl focus-visible:ring-2 focus-visible:ring-brand-amber"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="phone" className="text-sm font-bold">מספר טלפון</Label>
                  <Input
                    id="phone"
                    data-testid="form-input-phone"
                    placeholder="050-0000000"
                    value={form.phone}
                    type="tel"
                    onFocus={handleFormStart}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-bold">אימייל</Label>
                  <Input
                    id="email"
                    data-testid="form-input-email"
                    placeholder="name@email.com"
                    value={form.email}
                    type="email"
                    onFocus={handleFormStart}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 h-12 rounded-xl"
                  />
                </div>
              </div>

              {/* Marketing consent — explicit opt-in checkbox (§30א) */}
              <label
                htmlFor="marketing-consent"
                className="flex items-start gap-2.5 rounded-2xl bg-amber-50/60 border border-amber-200/70 px-3.5 py-2.5 cursor-pointer hover:bg-amber-50 transition-colors"
                data-testid="marketing-consent-wrapper"
              >
                <Checkbox
                  id="marketing-consent"
                  checked={marketingConsent}
                  onCheckedChange={(v) => setMarketingConsent(v === true)}
                  data-testid="marketing-consent-checkbox"
                  className="mt-0.5 border-brand-amber data-[state=checked]:bg-brand-amber data-[state=checked]:border-brand-amber flex-shrink-0"
                />
                <span className="text-xs sm:text-[13px] leading-[1.55] text-stone-700 select-none">
                  {(campaign?.marketing_consent_text || "").trim()
                    ? (campaign.marketing_consent_text || "").replace(/\{brand\}/g, campaign?.brand_name || "השירות")
                    : `אני מאשר/ת קבלת תוכן שיווקי, טיפים ועדכונים לגבי ${campaign?.brand_name || "השירות"} במייל וב-SMS. ניתן לבטל את הרישום בכל עת בלחיצה על "הסרה מרשימת התפוצה" בכל מייל שיישלח.`}
                  {" "}
                  <a
                    href={`/privacy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-brand-amber font-bold"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="marketing-consent-privacy-link"
                  >
                    מדיניות פרטיות
                  </a>
                </span>
              </label>

              <Button
                type="submit"
                data-testid="hero-submit-btn"
                disabled={submitting}
                className="w-full h-14 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white text-base font-bold pulse-cta transition-all active:scale-95"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    שולח...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {campaign.cta_primary}
                    <ArrowDown className="w-5 h-5 -rotate-90" />
                  </span>
                )}
              </Button>
              {section.cta_note ? (
                <p
                  data-testid="hero-cta-note"
                  className="text-center text-sm text-stone-600 leading-[1.55] px-1"
                >
                  {section.cta_note}
                </p>
              ) : null}
              {section.form_note ? (
                <p className="text-xs text-stone-500 text-center" dangerouslySetInnerHTML={{ __html: section.form_note }} />
              ) : null}
            </div>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative min-w-0 order-1 md:order-2"
        >
          <div className="absolute -inset-4 rounded-[40px] bg-gradient-to-tr from-amber-100 via-amber-50 to-transparent blur-2xl" />
          <img
            src={BOOK_MOCKUP}
            alt="ספר אמת או חובות"
            className="relative w-full h-auto rounded-[28px] shadow-[0_24px_60px_rgba(28,25,23,0.18)]"
            data-testid="hero-book-image"
          />
          {section.social_proof ? (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.7, type: "spring" }}
              className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 md:-top-2 md:-left-8 md:right-auto bg-white rounded-2xl shadow-[0_16px_40px_rgba(28,25,23,0.15)] border border-black/5 px-4 py-3 flex items-center gap-3 max-w-[280px] z-10"
              data-testid="hero-social-proof"
            >
              {/* stacked avatars */}
              <div className="flex -space-x-2 rtl:space-x-reverse flex-shrink-0">
                {["#F4C79E", "#D9A574", "#E88C4A"].map((c, i) => (
                  <span
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: c }}
                  >
                    <Users className="w-3.5 h-3.5" />
                  </span>
                ))}
              </div>
              <span className="text-sm font-semibold text-brand-charcoal leading-[1.35]">
                {section.social_proof}
              </span>
              {/* speech-bubble tail */}
              <span className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-black/5 rotate-45" />
            </motion.div>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}

function SectionRenderer({ section, campaign }) {
  switch (section.type) {
    case "pain":            return <PainSection section={section} campaign={campaign} />;
    case "explanation":     return <ExplanationSection section={section} />;
    case "questionnaire":   return <QuestionnaireSection section={section} />;
    case "stats":           return <StatsSection campaign={campaign} />;
    case "bonus":           return <BonusSection section={section} />;
    case "final_cta":       return <FinalCtaSection section={section} campaign={campaign} />;
    // Extended catalogue
    case "narrative":       return <NarrativeSection section={section} />;
    case "brand_intro":     return <BrandIntroSection section={section} />;
    case "benefits_cards":  return <BenefitsCardsSection section={section} />;
    case "wont_find":       return <WontFindSection section={section} />;
    case "aspirations":     return <AspirationsSection section={section} />;
    case "how_it_works":    return <HowItWorksSection section={section} />;
    case "faq":             return <FaqSection section={section} />;
    case "closing_slogan":  return <ClosingSloganSection section={section} />;
    default:                return null;
  }
}

function PainSection({ section, campaign }) {
  return (
    <section data-section="pain" className="bg-white border-y border-black/5">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="max-w-3xl mb-12">
          {section.eyebrow ? (
            <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">{section.eyebrow}</span>
          ) : null}
          <h2 className="text-3xl sm:text-4xl font-[800] leading-tight">{section.title}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {(campaign.pain_bullets || []).map((b, i) => (
            <motion.div
              key={b.title || `pain-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-[#fafaf9] rounded-3xl p-7 border border-black/5 hover:shadow-lg hover:-translate-y-1 transition-all"
              data-testid={`pain-bullet-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mb-5">
                <span className="text-2xl font-[800] text-brand-amber">{i + 1}</span>
              </div>
              <h3 className="text-xl font-[800] mb-3 leading-snug">{b.title}</h3>
              <p className="text-stone-600 leading-[1.6]">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ExplanationSection({ section }) {
  // Render `body` text with **bold** markdown → <strong>
  const parts = String(section.body || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <section data-section="explanation" className="bg-[#fafaf9]">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <h2 className="text-3xl sm:text-5xl font-[800] leading-tight max-w-3xl mx-auto">
          {section.title_main}
          {section.title_accent ? (
            <span className="block text-brand-amber mt-2">{section.title_accent}</span>
          ) : null}
        </h2>
        {section.body ? (
          <div className="mt-10 max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-black/5 text-right">
            <p className="text-lg leading-[1.8] text-stone-700">
              {parts.map((p, i) => (
                p.startsWith("**") && p.endsWith("**")
                  ? <strong key={i}>{p.slice(2, -2)}</strong>
                  : <span key={i}>{p}</span>
              ))}
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function QuestionnaireSection({ section }) {
  return (
    <section data-section="questionnaire" className="bg-white border-y border-black/5">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            {section.eyebrow ? (
              <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">{section.eyebrow}</span>
            ) : null}
            <h2 className="text-3xl sm:text-4xl font-[800] leading-tight">{section.title}</h2>
            <p className="mt-5 text-stone-600 leading-[1.7]">{section.intro}</p>
            <ul className="mt-6 space-y-3">
              {(section.bullets || []).map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="mt-1 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
                  </div>
                  <span className="text-stone-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gradient-to-br from-brand-charcoal to-brand-stone rounded-3xl p-8 md:p-10 text-white relative overflow-hidden">
            <div className="absolute -top-12 -left-12 w-48 h-48 rounded-full bg-brand-amber/30 blur-3xl" />
            <Sparkles className="relative w-10 h-10 text-brand-amber mb-4" />
            <h3 className="relative text-2xl font-[800] mb-3 leading-snug">{section.sidebar_title}</h3>
            <p className="relative text-stone-300 leading-[1.7]">{section.sidebar_text}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsSection({ campaign }) {
  return (
    <section data-section="stats" className="bg-[#fafaf9]">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {(campaign.stats || []).map((s, i) => {
            const Icon = STAT_ICONS[i % STAT_ICONS.length];
            return (
              <motion.div
                key={s.label || `stat-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-white rounded-2xl p-6 border border-black/5 text-center hover:shadow-lg hover:-translate-y-1 transition-all"
                data-testid={`stat-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-amber-50 mx-auto flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-brand-amber" />
                </div>
                <div className="text-3xl md:text-4xl font-[800] text-brand-charcoal">{s.value}</div>
                <div className="text-sm text-stone-600 mt-1">{s.label}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BonusSection({ section }) {
  return (
    <section data-section="bonus" className="bg-white border-y border-black/5">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="bg-gradient-to-tr from-amber-50 to-white rounded-3xl p-8 md:p-12 border border-amber-200/40 grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3">
            {section.badge ? (
              <span className="inline-block px-3 py-1 rounded-full bg-brand-amber text-white font-bold text-xs mb-4">{section.badge}</span>
            ) : null}
            <h2 className="text-3xl sm:text-4xl font-[800] leading-tight">
              {section.title_prefix} {section.title_accent ? <span className="text-brand-amber">{section.title_accent}</span> : null}
            </h2>
            <p className="mt-5 text-stone-700 leading-[1.7]">{section.body}</p>
            {(section.chips || []).length > 0 ? (
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                {section.chips.map((t) => (
                  <span key={t} className="bg-white px-3 py-1.5 rounded-full border border-amber-200">{t}</span>
                ))}
              </div>
            ) : null}
          </div>
          <div className="md:col-span-2">
            <img src={COMMUNITY_MOCKUP} alt="קהילת וואטסאפ" className="w-full h-auto rounded-2xl shadow-xl" />
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCtaSection({ section, campaign }) {
  return (
    <section className="bg-gradient-to-b from-[#fafaf9] to-white">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-center">
        <h2 className="text-3xl sm:text-5xl font-[800] leading-tight">{section.title}</h2>
        <p className="mt-5 text-lg text-stone-600">{section.subtitle}</p>
        <a
          href="#hero-form"
          data-testid="bottom-cta-btn"
          className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold pulse-cta transition active:scale-95"
        >
          {campaign.cta_secondary}
          <ChevronDown className="w-5 h-5" />
        </a>
      </div>
    </section>
  );
}

/* ==========================================================================
 * Extended section catalogue — designed for the "hidden-debts" campaign
 * (docx spec: 13.07.26). Reusable by future campaigns.
 * All sections respect the site palette: bg-[#fafaf9] / white / brand-amber /
 * brand-charcoal. RTL Hebrew. Framer-motion entrance animations.
 * ========================================================================== */

/**
 * Narrative — flexible long-form section: title + multi-paragraph body +
 * optional large "punch" quote at the end.
 * Used for: story_outside, story_not_failure, brand_story.
 */
function NarrativeSection({ section }) {
  const paragraphs = Array.isArray(section.paragraphs) ? section.paragraphs : [];
  return (
    <section
      data-section={section.id}
      className="bg-white border-y border-black/5"
      data-testid={`section-${section.id}`}
    >
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          {section.eyebrow ? (
            <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">
              {section.eyebrow}
            </span>
          ) : null}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] text-brand-charcoal">
            {section.title}
          </h2>
          {section.subtitle ? (
            <p className="mt-3 text-xl md:text-2xl text-stone-500 font-semibold leading-[1.4]">
              {section.subtitle}
            </p>
          ) : null}

          <div className="mt-8 space-y-5">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-lg leading-[1.85] text-stone-700 whitespace-pre-line">
                {p}
              </p>
            ))}
          </div>

          {section.punch ? (
            <div className="mt-10 relative bg-[#fafaf9] rounded-3xl border-r-4 border-brand-amber px-6 md:px-8 py-6 md:py-8">
              <p className="text-xl md:text-2xl font-[800] leading-[1.4] text-brand-charcoal">
                {section.punch}
              </p>
            </div>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}

/**
 * BrandIntro — welcome + promise chips (bli/bli/bli) + closing body.
 */
function BrandIntroSection({ section }) {
  return (
    <section
      data-section={section.id}
      className="relative overflow-hidden bg-gradient-to-br from-[#fafaf9] via-amber-50/40 to-[#fafaf9]"
      data-testid={`section-${section.id}`}
    >
      <div className="absolute inset-0 amber-halo opacity-70 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        {section.eyebrow ? (
          <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">
            {section.eyebrow}
          </span>
        ) : null}
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] text-brand-charcoal max-w-3xl mx-auto">
          {section.title}
        </h2>
        {section.body ? (
          <p className="mt-6 text-lg leading-[1.7] text-stone-700 max-w-2xl mx-auto whitespace-pre-line">
            {section.body}
          </p>
        ) : null}
        {(section.chips || []).length > 0 ? (
          <div className="mt-8 flex flex-wrap justify-center gap-2 md:gap-3">
            {section.chips.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-2 bg-white text-brand-charcoal font-semibold px-4 py-2 rounded-full border border-amber-200/70 shadow-sm text-sm"
              >
                <XIcon className="w-4 h-4 text-brand-amber" strokeWidth={3} />
                {t}
              </span>
            ))}
          </div>
        ) : null}
        {section.closing_body ? (
          <p className="mt-8 text-lg leading-[1.7] text-stone-700 max-w-2xl mx-auto whitespace-pre-line">
            {section.closing_body}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * BenefitsCards — 3 numbered value cards + CTA (scrolls to hero form) + note.
 */
function BenefitsCardsSection({ section }) {
  const cards = Array.isArray(section.cards) ? section.cards : [];
  const CARD_ICONS = [Eye, Heart, Wrench];
  return (
    <section
      data-section={section.id}
      className="bg-[#fafaf9]"
      data-testid={`section-${section.id}`}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
        {section.title ? (
          <div className="text-center max-w-3xl mx-auto mb-12">
            {section.eyebrow ? (
              <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">
                {section.eyebrow}
              </span>
            ) : null}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.2] text-brand-charcoal">
              {section.title}
            </h2>
          </div>
        ) : null}
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c, i) => {
            const Icon = CARD_ICONS[i % CARD_ICONS.length];
            return (
              <motion.div
                key={c.heading || `benefit-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-3xl p-7 border border-black/5 shadow-[0_8px_28px_rgba(0,0,0,0.04)] hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid={`benefit-card-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-brand-amber" strokeWidth={2.5} />
                  </div>
                  <span className="text-3xl font-[800] text-brand-amber/40 leading-none">0{i + 1}</span>
                </div>
                <h3 className="text-xl font-[800] mb-3 leading-snug text-brand-charcoal">
                  {c.heading}
                </h3>
                <p className="text-stone-600 leading-[1.7]">{c.bullet}</p>
              </motion.div>
            );
          })}
        </div>
        {section.cta_text ? (
          <div className="mt-12 text-center">
            <a
              href="#hero-form"
              data-testid={`section-${section.id}-cta`}
              className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold pulse-cta transition active:scale-95"
            >
              {section.cta_text}
              <ArrowDown className="w-5 h-5" />
            </a>
            {section.cta_note ? (
              <p className="mt-4 text-sm text-stone-500 leading-[1.6] max-w-lg mx-auto">
                {section.cta_note}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * WontFind — negative bullets (X icons) followed by a "truth" closing.
 */
function WontFindSection({ section }) {
  const bullets = Array.isArray(section.bullets) ? section.bullets : [];
  return (
    <section
      data-section={section.id}
      className="bg-white border-y border-black/5"
      data-testid={`section-${section.id}`}
    >
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
        >
          {section.eyebrow ? (
            <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">
              {section.eyebrow}
            </span>
          ) : null}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] text-brand-charcoal">
            {section.title}
          </h2>
          <ul className="mt-10 space-y-4">
            {bullets.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-start gap-4 bg-[#fafaf9] rounded-2xl px-5 py-4 border border-black/5"
              >
                <div className="mt-0.5 w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <XIcon className="w-4 h-4 text-rose-600" strokeWidth={3} />
                </div>
                <span className="text-lg text-stone-700 leading-[1.6]">{b}</span>
              </motion.li>
            ))}
          </ul>
          {section.closing ? (
            <div className="mt-10 text-center">
              <p className="text-xl md:text-2xl font-[800] leading-[1.4] text-brand-charcoal">
                {section.closing}
              </p>
            </div>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}

/**
 * Aspirations — question title + 6 bullets in a 2-column grid + goal statement.
 */
function AspirationsSection({ section }) {
  const bullets = Array.isArray(section.bullets) ? section.bullets : [];
  return (
    <section
      data-section={section.id}
      className="bg-[#fafaf9]"
      data-testid={`section-${section.id}`}
    >
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-10">
          {section.eyebrow ? (
            <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">
              {section.eyebrow}
            </span>
          ) : null}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] text-brand-charcoal">
            {section.title}
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-3 md:gap-4">
          {bullets.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.06 }}
              className="flex items-start gap-3 bg-white rounded-2xl px-5 py-4 border border-black/5 shadow-[0_4px_16px_rgba(0,0,0,0.03)]"
            >
              <Heart className="w-5 h-5 text-brand-amber flex-shrink-0 mt-1" strokeWidth={2.5} />
              <span className="text-base md:text-lg text-stone-700 leading-[1.5]">{b}</span>
            </motion.div>
          ))}
        </div>
        {section.goal_text ? (
          <div className="mt-10 max-w-3xl mx-auto text-center bg-white rounded-3xl border border-amber-200/60 px-6 md:px-8 py-6 md:py-8">
            <p className="text-lg md:text-xl leading-[1.65] text-brand-charcoal font-semibold whitespace-pre-line">
              {section.goal_text}
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * HowItWorks — 3 numbered steps + closing note.
 */
function HowItWorksSection({ section }) {
  const steps = Array.isArray(section.steps) ? section.steps : [];
  return (
    <section
      data-section={section.id}
      className="bg-white border-y border-black/5"
      data-testid={`section-${section.id}`}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12">
          {section.eyebrow ? (
            <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">
              {section.eyebrow}
            </span>
          ) : null}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] text-brand-charcoal">
            {section.title}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 relative">
          {/* Connector line (desktop) */}
          <div className="hidden md:block absolute top-8 right-[16.66%] left-[16.66%] h-0.5 bg-gradient-to-l from-transparent via-amber-200 to-transparent -z-0" />
          {steps.map((s, i) => (
            <motion.div
              key={s.heading || `step-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="relative bg-[#fafaf9] rounded-3xl p-7 border border-black/5 text-center hover:shadow-lg transition-shadow"
              data-testid={`step-card-${i}`}
            >
              <div className="w-16 h-16 rounded-full bg-brand-amber text-white flex items-center justify-center mx-auto mb-5 shadow-lg text-2xl font-[800]">
                {i + 1}
              </div>
              <h3 className="text-xl font-[800] mb-3 text-brand-charcoal leading-snug">
                {s.heading}
              </h3>
              <p className="text-stone-600 leading-[1.7] text-base">{s.body}</p>
            </motion.div>
          ))}
        </div>
        {section.closing ? (
          <p className="mt-10 text-center text-stone-500 text-sm md:text-base">{section.closing}</p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * FAQ — accordion of Q&A pairs. Each item is independently expandable.
 */
function FaqSection({ section }) {
  const items = Array.isArray(section.items) ? section.items : [];
  const [openIdx, setOpenIdx] = useState(-1);
  return (
    <section
      data-section={section.id}
      className="bg-[#fafaf9]"
      data-testid={`section-${section.id}`}
    >
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="mb-10 text-center">
          {section.eyebrow ? (
            <span className="inline-block text-brand-amber font-bold text-sm tracking-widest mb-3">
              {section.eyebrow}
            </span>
          ) : null}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-[800] leading-[1.15] text-brand-charcoal">
            {section.title}
          </h2>
        </div>
        <div className="space-y-3">
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={item.q || `faq-${i}`}
                className={`bg-white rounded-2xl border overflow-hidden transition-colors ${
                  open ? "border-brand-amber shadow-[0_8px_28px_rgba(217,119,6,0.08)]" : "border-black/5"
                }`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full text-right px-5 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4 hover:bg-stone-50 transition-colors"
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={open}
                >
                  <span className="text-base md:text-lg font-[700] text-brand-charcoal leading-snug">
                    {item.q}
                  </span>
                  <span
                    className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-colors ${
                      open ? "bg-brand-amber text-white" : "bg-amber-50 text-brand-amber"
                    }`}
                  >
                    {open ? <MinusIcon className="w-4 h-4" strokeWidth={3} /> : <PlusIcon className="w-4 h-4" strokeWidth={3} />}
                  </span>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <p className="px-5 md:px-6 pb-5 md:pb-6 text-stone-700 leading-[1.75] text-base">
                    {item.a}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * ClosingSlogan — quiet, high-impact closer under the final CTA.
 */
function ClosingSloganSection({ section }) {
  return (
    <section
      data-section={section.id}
      className="bg-brand-charcoal text-white"
      data-testid={`section-${section.id}`}
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
        <p className="text-2xl md:text-3xl font-[800] leading-[1.35] tracking-tight">
          {section.title}
        </p>
        {section.subtitle ? (
          <p className="mt-3 text-brand-amber text-lg md:text-xl font-[700]">{section.subtitle}</p>
        ) : null}
      </div>
    </section>
  );
}
